import Analytics from '@/utils/analytics';
import {
  getUpdatedOrder,
  useNewOrderActionsContext,
} from '@/contexts/new-order-actions-context';
import { QuantityMapType } from '@/screens/new-order/hooks/use-quantity-reducer';
import { SMART_ORDER_TRACKING_KEY } from '@/components/orders/smart-order-result';
import { SmartOrderState } from '@/components/orders/hooks/use-smart-order-reducer';
import { useCallback, useState } from 'react';
import { useUserContext } from '@/contexts/user-context';

interface Props {
  state: SmartOrderState;
}

export const useCreateSmartOrder = ({ state }: Props) => {
  const {
    selectedRestaurantRelationship: { supplierUUID },
    selectedRestaurantUUID: restaurantUUID,
  } = useUserContext();
  const { updateAndSetQuantity } = useNewOrderActionsContext();

  const [addToCartLoading, setAddToCartLoading] = useState(false);

  const onAddToCart = useCallback(
    async (smartOrderUUID: string) => {
      setAddToCartLoading(true);

      const orderedQuantityMap: QuantityMapType =
        state.groupedResults.reduceRight(
          (quantityMap, result) => ({
            ...quantityMap,
            ...result.variantPacks.reduceRight((acc, vp) => {
              const quantity = state.quantityMap[vp.uuid];
              if (quantity > 0) {
                return {
                  ...acc,
                  [vp.uuid]: quantity,
                };
              }

              return acc;
            }, {}),
          }),
          {},
        );

      try {
        const result = await updateAndSetQuantity(
          SMART_ORDER_TRACKING_KEY,
          orderedQuantityMap,
        );

        const order = getUpdatedOrder(result);
        if (!order) {
          return false;
        }

        Analytics.track('smart_order.add_to_order_button.press', {
          orderUUID: order.uuid,
          requestedItems: state.requestedItems,
          restaurantUUID,
          smartOrderUUID,
          supplierUUID,
        });

        return true;
      } finally {
        setAddToCartLoading(false);
      }
    },
    [restaurantUUID, state, supplierUUID, updateAndSetQuantity],
  );

  return {
    addToCartLoading,
    onAddToCart,
  };
};
